import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import {
  Receipt,
  Plus,
  Search,
  Filter,
  Plane,
  CreditCard,
  Building2,
  Eye,
  CheckCircle,
  Clock,
  Calendar,
  MapPin,
  User,
  ArrowRight,
  BookmarkCheck,
  CheckCircle2,
  AlertCircle,
  Paperclip,
  Briefcase,
  Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../constants/permissions';
import TravelRequestDetailsModal from './TravelRequestDetailsModal';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelBookingDesk = ({ defaultTab = 'assigned' }) => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || defaultTab || 'assigned';
  const [loading, setLoading] = useState(true);
  const [viewTab, setViewTab] = useState(initialTab); // 'assigned' | 'confirmed'
  const [bookings, setBookings] = useState([]);
  const [assignedRequests, setAssignedRequests] = useState([]);
  const [ticketRequests, setTicketRequests] = useState([]);
  const [requests, setRequests] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [bookingStatusChoice, setBookingStatusChoice] = useState('booked'); // 'booked' | 'not_booked'

  const [bookingForm, setBookingForm] = useState({
    request_id: '',
    ticket_request_id: '',
    ticket_number: '',
    booking_type: 'flight',
    pnr_reference: '',
    vendor_name: 'IndiGo Airlines',
    carrier_name_or_no: '6E-204',
    base_fare: 10000,
    taxes: 1800,
    convenience_fee: 200,
    booking_date: new Date().toISOString().split('T')[0],
    ticket_attachment_url: '',
    credit_card_used_id: '',
    cancellation_policy: 'Non-refundable within 24 hrs',
    not_booked_reason: '',
  });

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/bookings`, { withCredentials: true });
      setBookings(data.bookings || []);
    } catch (err) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTicketRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/ticket-requests?status=approved`, { withCredentials: true });
      setTicketRequests(data.ticket_requests || []);
    } catch (err) {
      console.warn('Failed to load ticket requests', err);
    }
  }, []);

  const fetchAssignedRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/booking-controller-requests`, { withCredentials: true });
      setAssignedRequests(data.requests || []);
    } catch (err) {
      console.warn('Failed to load assigned controller requests', err);
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/requests`, { withCredentials: true });
      const approved = (data.requests || []).filter((r) => ['approved', 'booked'].includes(r.status));
      setRequests(approved);
      if (approved.length > 0 && !bookingForm.request_id) {
        setBookingForm((prev) => ({ ...prev, request_id: approved[0]._id }));
      }
    } catch (err) {}
  }, [bookingForm.request_id]);

  const fetchCreditCards = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/travel-desk/credit-cards`, { withCredentials: true });
      setCreditCards(data.credit_cards || []);
    } catch (err) {}
  }, []);

  useEffect(() => {
    fetchBookings();
    fetchAssignedRequests();
    fetchTicketRequests();
    fetchRequests();
    fetchCreditCards();
  }, [fetchBookings, fetchAssignedRequests, fetchTicketRequests, fetchRequests, fetchCreditCards]);

  const handleSaveBooking = async (e) => {
    e.preventDefault();
    try {
      if (bookingStatusChoice === 'not_booked') {
        if (!bookingForm.not_booked_reason?.trim()) {
          toast.error('Please enter the reason for not booking the ticket.');
          return;
        }
        await axios.post(`${API_URL}/travel-desk/bookings`, {
          request_id: bookingForm.request_id,
          booking_status: 'not_booked',
          booking_action: 'not_booked',
          not_booked_reason: bookingForm.not_booked_reason.trim()
        }, { withCredentials: true });
        toast.success('Recorded: Ticket Not Booked');
      } else {
        if (!bookingForm.pnr_reference?.trim()) {
          toast.error('Please enter the PNR / Reference Number.');
          return;
        }
        if (bookingForm.ticket_request_id) {
          // Fulfill specific Ticket Booking Request
          await axios.post(`${API_URL}/travel-desk/requests/${bookingForm.request_id}/ticket-requests/${bookingForm.ticket_request_id}/book`, {
            ...bookingForm,
            booking_status: 'confirmed'
          }, { withCredentials: true });
          toast.success(`Ticket for request ${bookingForm.ticket_request_id} recorded successfully!`);
        } else {
          await axios.post(`${API_URL}/travel-desk/bookings`, {
            ...bookingForm,
            booking_status: 'confirmed'
          }, { withCredentials: true });
          toast.success('Ticket booking recorded!');
        }
      }
      setShowBookingModal(false);
      fetchBookings();
      fetchAssignedRequests();
      fetchTicketRequests();
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save booking');
    }
  };

  const openBookingForRequest = (req) => {
    const flightLeg = (req.journey_details || [])[0];
    const carrier = flightLeg?.carrier_number || flightLeg?.flight_number || flightLeg?.train_number || '6E-204';
    const mode = (req.travel_type || req.travel_mode || 'flight').toLowerCase();
    const isAlreadyNotBooked = req.ticket_booking_status === 'not_booked' || req.booking_status === 'not_booked';

    setBookingStatusChoice(isAlreadyNotBooked ? 'not_booked' : 'booked');
    setBookingForm((prev) => ({
      ...prev,
      request_id: req._id,
      booking_type: mode === 'train' ? 'train' : (mode === 'cab' || mode === 'car' ? 'cab' : 'flight'),
      carrier_name_or_no: carrier,
      vendor_name: mode === 'train' ? 'IRCTC Corporate' : 'IndiGo Airlines',
      not_booked_reason: req.ticket_not_booked_reason || req.ticket_booking_info?.not_booked_reason || '',
    }));
    setShowBookingModal(true);
  };

  const openBookingForTicketRequest = (tbr) => {
    const mode = (tbr.travel_mode || 'flight').toLowerCase();
    setBookingStatusChoice('booked');
    setBookingForm({
      request_id: tbr.travel_request_id,
      ticket_request_id: tbr.ticket_request_id,
      ticket_number: '',
      booking_type: mode === 'train' ? 'train' : (mode === 'bus' ? 'bus' : 'flight'),
      pnr_reference: '',
      vendor_name: mode === 'train' ? 'IRCTC Corporate' : (mode === 'bus' ? 'Intercity Bus' : 'IndiGo Airlines'),
      carrier_name_or_no: mode === 'train' ? '12601' : '6E-204',
      base_fare: Number(tbr.estimated_ticket_amount) || 5000,
      taxes: 0,
      convenience_fee: 0,
      booking_date: new Date().toISOString().split('T')[0],
      ticket_attachment_url: '',
      credit_card_used_id: '',
      cancellation_policy: 'Standard Cancellation Policy',
      not_booked_reason: '',
    });
    setShowBookingModal(true);
  };

  const handleOpenDetails = (reqId) => {
    setSelectedRequestId(reqId);
    setDetailsModalOpen(true);
  };

  // Filtered assigned requests
  const filteredAssigned = assignedRequests.filter((r) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (r.request_number || '').toLowerCase().includes(q) ||
      (r.employee_name || '').toLowerCase().includes(q) ||
      (r.employee_code || '').toLowerCase().includes(q) ||
      (r.requester_name || '').toLowerCase().includes(q) ||
      (r.department || r.department_name || '').toLowerCase().includes(q) ||
      (r.reporting_manager_name || '').toLowerCase().includes(q) ||
      (r.employee_category || '').toLowerCase().includes(q) ||
      (r.branch_name || '').toLowerCase().includes(q) ||
      (r.company_name || '').toLowerCase().includes(q) ||
      (r.purpose || '').toLowerCase().includes(q) ||
      (r.origin_city || '').toLowerCase().includes(q) ||
      (r.destination_city || '').toLowerCase().includes(q)
    );
  });

  // Filtered confirmed bookings
  const filteredBookings = bookings.filter((b) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (b.booking_number || '').toLowerCase().includes(q) ||
      (b.pnr_reference || '').toLowerCase().includes(q) ||
      (b.employee_name || '').toLowerCase().includes(q) ||
      (b.carrier_name_or_no || '').toLowerCase().includes(q) ||
      (b.vendor_name || '').toLowerCase().includes(q)
    );
  });

  // Filtered approved ticket booking requests
  const filteredTicketRequests = ticketRequests.filter((t) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (t.ticket_request_id || '').toLowerCase().includes(q) ||
      (t.travel_request_number || '').toLowerCase().includes(q) ||
      (t.employee_name || '').toLowerCase().includes(q) ||
      (t.employee_code || '').toLowerCase().includes(q) ||
      (t.from_location || '').toLowerCase().includes(q) ||
      (t.to_location || '').toLowerCase().includes(q)
    );
  });

  const canBook = hasPermission(user, 'travel_desk.booking.edit') || user?.role !== 'viewer';

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
            <Receipt className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black">Booking Desk Operations</h1>
            <p className="text-xs text-blue-200">
              Manage assigned travel bookings, record PNRs, carrier details, and corporate card billing.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {canBook && (
            <button
              onClick={() => setShowBookingModal(true)}
              className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs rounded-xl shadow-md flex items-center gap-2 transition-all cursor-pointer"
            >
              <Plus className="w-4 h-4" /> Book Approved Request
            </button>
          )}
        </div>
      </div>

      {/* View Switcher & Search Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-xl border border-slate-200">
          <button
            onClick={() => setViewTab('assigned')}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${
              viewTab === 'assigned'
                ? 'bg-white text-indigo-950 shadow-xs border border-slate-200'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <BookmarkCheck className="w-4 h-4 text-indigo-600" />
            My Assigned Applications (My Booking Queue)
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-indigo-100 text-indigo-800 font-bold">
              {assignedRequests.length}
            </span>
          </button>

          <button
            onClick={() => setViewTab('confirmed')}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${
              viewTab === 'confirmed'
                ? 'bg-white text-slate-900 shadow-xs border border-slate-200'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            Confirmed Bookings Register
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-emerald-100 text-emerald-800 font-bold">
              {bookings.length}
            </span>
          </button>

          <button
            onClick={() => setViewTab('ticket_requests')}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${
              viewTab === 'ticket_requests'
                ? 'bg-white text-blue-900 shadow-xs border border-slate-200'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Plane className="w-4 h-4 text-blue-600" />
            Approved Ticket Booking Requests
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-blue-100 text-blue-800 font-bold">
              {ticketRequests.length}
            </span>
          </button>
        </div>

        <div className="relative min-w-[280px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder={viewTab === 'assigned' ? 'Search assigned requests...' : 'Search confirmed bookings...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs outline-none focus:border-indigo-500 shadow-xs"
          />
        </div>
      </div>

      {/* Tab 1: My Assigned Applications / Booking Queue Table */}
      {viewTab === 'assigned' && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 bg-slate-50 border-b border-slate-200 font-bold text-xs text-slate-800 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <BookmarkCheck className="w-4 h-4 text-indigo-600" />
              Eligible Travel Applications Assigned to You ({filteredAssigned.length})
            </span>
            <span className="text-[11px] text-slate-500 font-normal">Department mapped, category, or reporting hierarchy assigned</span>
          </div>

          {loading ? (
            <div className="p-8 text-center text-slate-500 font-medium">Loading assigned requests...</div>
          ) : filteredAssigned.length === 0 ? (
            <div className="p-12 text-center text-slate-400 font-semibold italic">
              No pending travel requests assigned to you at this time.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px]">
                  <tr>
                    <th className="p-3">Application #</th>
                    <th className="p-3">Employee</th>
                    <th className="p-3">Manager &amp; Department</th>
                    <th className="p-3">Category</th>
                    <th className="p-3">Company &amp; Branch</th>
                    <th className="p-3">Purpose &amp; Itinerary</th>
                    <th className="p-3">Dates &amp; Mode</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Documents</th>
                    <th className="p-3 text-right">Authorized Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                  {filteredAssigned.map((r) => {
                    const leg = (r.journey_details || [])[0];
                    const fromCity = r.origin_city || leg?.from_city || 'Origin';
                    const toCity = r.destination_city || leg?.to_city || 'Destination';
                    const bStatus = r.booking_status || (r.status === 'booked' ? 'confirmed' : 'pending');
                    const attCount = (r.attachments || []).length;

                    return (
                      <tr key={r._id} className="hover:bg-slate-50 transition-colors">
                        {/* Application / Request # */}
                        <td className="p-3">
                          <div className="font-mono font-bold text-indigo-600">{r.request_number}</div>
                          <div className="text-[10px] text-slate-400">{r.booking_type || 'Travel'}</div>
                        </td>

                        {/* Requesting Employee */}
                        <td className="p-3">
                          <div className="font-bold text-slate-900">{r.employee_name || 'Staff'}</div>
                          <div className="text-[10px] text-slate-400 font-mono">{r.employee_code || '—'}</div>
                          {r.requester_name && r.requester_name !== r.employee_name && (
                            <div className="text-[9px] text-indigo-500">By: {r.requester_name}</div>
                          )}
                        </td>

                        {/* Manager & Department */}
                        <td className="p-3">
                          <div className="font-semibold text-slate-800">{r.department || r.department_name || 'General'}</div>
                          <div className="text-[10px] text-slate-500">Mgr: {r.reporting_manager_name || '—'}</div>
                        </td>

                        {/* Employee Category */}
                        <td className="p-3">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
                            {r.employee_category || r.category || 'Staff'}
                          </span>
                        </td>

                        {/* Company & Branch */}
                        <td className="p-3">
                          <div className="text-slate-800">{r.company_name || r.company_id || 'Company'}</div>
                          <div className="text-[10px] text-slate-500">{r.branch_name || r.location || 'Headquarters'}</div>
                        </td>

                        {/* Purpose & Itinerary */}
                        <td className="p-3 max-w-[200px]">
                          <div className="font-semibold text-slate-800 truncate" title={r.purpose}>{r.purpose || 'Outstation Travel'}</div>
                          <div className="text-[10px] text-slate-600 flex items-center gap-1 mt-0.5">
                            <span>{fromCity}</span>
                            <span className="text-slate-400">→</span>
                            <span>{toCity}</span>
                          </div>
                        </td>

                        {/* Travel Dates & Mode */}
                        <td className="p-3">
                          <div className="font-mono text-[11px] whitespace-nowrap">
                            {r.start_date || '—'} {r.end_date ? `→ ${r.end_date}` : ''}
                          </div>
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-blue-50 text-blue-700 border border-blue-200 mt-0.5 inline-block">
                            {r.travel_type || r.travel_mode || 'Flight'}
                          </span>
                        </td>

                        {/* Approval & Booking Status */}
                        <td className="p-3 space-y-1">
                          <div>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 uppercase">
                              {r.approval_status || r.status || 'Approved'}
                            </span>
                          </div>
                          <div>
                            {r.ticket_booking_status === 'not_booked' || r.booking_status === 'not_booked' ? (
                              <div className="space-y-0.5">
                                <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-amber-100 text-amber-900 border border-amber-300 inline-block">
                                  Ticket Not Booked
                                </span>
                                {(r.ticket_not_booked_reason || r.ticket_booking_info?.not_booked_reason) && (
                                  <div className="text-[10px] text-amber-800 italic max-w-[170px] truncate" title={r.ticket_not_booked_reason || r.ticket_booking_info?.not_booked_reason}>
                                    Reason: {r.ticket_not_booked_reason || r.ticket_booking_info?.not_booked_reason}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span
                                className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${
                                  bStatus === 'confirmed' || bStatus === 'booked'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : 'bg-slate-100 text-slate-700'
                                }`}
                              >
                                {bStatus === 'confirmed' || bStatus === 'booked' ? 'Ticket Booked' : 'Pending Booking'}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Supporting Documents */}
                        <td className="p-3">
                          {attCount > 0 ? (
                            <button
                              type="button"
                              onClick={() => handleOpenDetails(r._id)}
                              className="px-2 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-[10px] font-bold inline-flex items-center gap-1 border border-indigo-200 transition-colors"
                              title="Click to view supporting documents"
                            >
                              <Paperclip className="w-3 h-3" />
                              {attCount} file{attCount > 1 ? 's' : ''}
                            </button>
                          ) : (
                            <span className="text-[11px] text-slate-400 italic">None</span>
                          )}
                        </td>

                        {/* Authorized Actions */}
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleOpenDetails(r._id)}
                              className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-[11px] font-bold flex items-center gap-1 transition-all cursor-pointer"
                              title="View Application Details"
                            >
                              <Eye className="w-3.5 h-3.5 text-slate-500" /> Details
                            </button>
                            {canBook && (
                              <button
                                type="button"
                                onClick={() => openBookingForRequest(r)}
                                className={`px-3 py-1.5 rounded-lg text-[11px] font-extrabold flex items-center gap-1 shadow-xs transition-all cursor-pointer ${
                                  r.ticket_booking_status === 'not_booked' || r.booking_status === 'not_booked'
                                    ? 'bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300'
                                    : bStatus === 'confirmed' || bStatus === 'booked'
                                    ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200'
                                    : 'bg-amber-500 hover:bg-amber-400 text-slate-950'
                                }`}
                                title="Process or Update Booking"
                              >
                                <Receipt className="w-3.5 h-3.5" />
                                {r.ticket_booking_status === 'not_booked' || r.booking_status === 'not_booked'
                                  ? 'Update / Enter Ticket'
                                  : bStatus === 'confirmed' || bStatus === 'booked'
                                  ? 'View Booking'
                                  : 'Book Ticket'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Confirmed Ticket & Booking Register Table */}
      {viewTab === 'confirmed' && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 bg-slate-50 border-b border-slate-200 font-bold text-xs text-slate-800">
            Confirmed Ticket &amp; Booking Register ({filteredBookings.length})
          </div>

          {loading ? (
            <div className="p-8 text-center text-slate-500 font-medium">Loading bookings...</div>
          ) : filteredBookings.length === 0 ? (
            <div className="p-12 text-center text-slate-400 font-semibold italic">No bookings recorded yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px]">
                  <tr>
                    <th className="p-3.5">Booking Ref</th>
                    <th className="p-3.5">PNR</th>
                    <th className="p-3.5">Employee</th>
                    <th className="p-3.5">Carrier / Flight</th>
                    <th className="p-3.5">Vendor</th>
                    <th className="p-3.5">Credit Card</th>
                    <th className="p-3.5 text-right">Total Amount</th>
                    <th className="p-3.5">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                  {filteredBookings.map((b) => (
                    <tr key={b._id} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3.5 font-mono font-bold text-blue-600">{b.booking_number}</td>
                      <td className="p-3.5 font-mono font-bold text-slate-900">{b.pnr_reference}</td>
                      <td className="p-3.5 font-bold">{b.employee_name}</td>
                      <td className="p-3.5">{b.carrier_name_or_no}</td>
                      <td className="p-3.5">{b.vendor_name}</td>
                      <td className="p-3.5 font-mono text-slate-600">{b.credit_card_masked}</td>
                      <td className="p-3.5 font-mono font-bold text-emerald-700 text-right">
                        ₹ {(b.total_amount || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="p-3.5">
                        <span className="px-2.5 py-1 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 uppercase">
                          {b.booking_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab 3: Approved Ticket Booking Requests */}
      {viewTab === 'ticket_requests' && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 bg-slate-50 border-b border-slate-200 font-bold text-xs text-slate-800 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Plane className="w-4 h-4 text-blue-600" />
              Approved Outstation Ticket Booking Requests ({filteredTicketRequests.length})
            </span>
            <span className="text-slate-500 font-normal">
              Tickets requested by employees, approved by managers, awaiting booking fulfillment
            </span>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-500 font-semibold">Loading ticket requests...</div>
          ) : filteredTicketRequests.length === 0 ? (
            <div className="p-12 text-center text-slate-400 italic font-medium">
              No approved ticket booking requests pending fulfillment.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px] border-b border-slate-200">
                  <tr>
                    <th className="p-3.5">Ticket Req ID</th>
                    <th className="p-3.5">Travel Request &amp; Employee</th>
                    <th className="p-3.5">Sector &amp; Travel Dates</th>
                    <th className="p-3.5">Mode &amp; Type</th>
                    <th className="p-3.5">Passenger Manifest</th>
                    <th className="p-3.5 text-right">Est. Fare</th>
                    <th className="p-3.5">Status</th>
                    <th className="p-3.5 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                  {filteredTicketRequests.map((t) => (
                    <tr key={t._id || t.ticket_request_id} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3.5 font-mono font-bold text-blue-700 whitespace-nowrap">
                        {t.ticket_request_id}
                      </td>
                      <td className="p-3.5">
                        <div className="font-bold text-slate-900">{t.travel_request_number}</div>
                        <div className="text-[11px] text-slate-500">{t.employee_name} ({t.employee_code})</div>
                      </td>
                      <td className="p-3.5">
                        <div className="font-bold text-slate-900">{t.from_location} → {t.to_location}</div>
                        <div className="text-[11px] text-slate-500 font-mono">
                          {t.departure_date} {t.return_date ? `to ${t.return_date}` : ''}
                        </div>
                      </td>
                      <td className="p-3.5">
                        <span className="font-bold text-slate-800">{t.travel_mode}</span>
                        <div className="text-[11px] text-slate-500">{t.booking_type} • {t.preferred_travel_time}</div>
                      </td>
                      <td className="p-3.5">
                        <div className="font-semibold text-slate-900">{t.passenger_details?.length || 1} Passenger(s)</div>
                        <div className="text-[11px] text-slate-500 truncate max-w-[160px]">
                          {(t.passenger_details || []).map(p => p.name).join(', ')}
                        </div>
                      </td>
                      <td className="p-3.5 text-right font-mono font-bold text-slate-900 whitespace-nowrap">
                        ₹ {(Number(t.estimated_ticket_amount) || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="p-3.5 whitespace-nowrap">
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black bg-indigo-100 text-indigo-800 border border-indigo-200">
                          {t.status_label || 'Approved (Booking Pending)'}
                        </span>
                      </td>
                      <td className="p-3.5 text-center whitespace-nowrap">
                        <div className="flex items-center justify-center gap-1.5">
                          {canBook && (
                            <button
                              onClick={() => openBookingForTicketRequest(t)}
                              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-xs flex items-center gap-1 cursor-pointer"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" /> Book Ticket
                            </button>
                          )}
                          <button
                            onClick={() => handleOpenDetails(t.travel_request_id)}
                            className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs flex items-center gap-1 cursor-pointer"
                            title="View Dossier"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <Receipt className="w-5 h-5 text-amber-500" /> Book Approved Travel Request
              </h3>
              <button onClick={() => setShowBookingModal(false)} className="text-slate-400 hover:text-slate-600 font-bold text-lg">
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveBooking} className="space-y-3.5 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Select Travel Request</label>
                <select
                  value={bookingForm.request_id}
                  onChange={(e) => setBookingForm({ ...bookingForm, request_id: e.target.value })}
                  className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-bold"
                  required
                >
                  <option value="">-- Choose Approved Travel Request --</option>
                  {/* Prioritize assigned requests */}
                  {assignedRequests.map((r) => (
                    <option key={r._id} value={r._id}>
                      {r.request_number} — {r.employee_name} ({r.purpose || 'Travel'})
                    </option>
                  ))}
                  {/* Other requests */}
                  {requests
                    .filter((r) => !assignedRequests.some((ar) => ar._id === r._id))
                    .map((r) => (
                      <option key={r._id} value={r._id}>
                        {r.request_number} — {r.employee_name} ({r.purpose || 'Travel'})
                      </option>
                    ))}
                </select>
              </div>

              {/* TICKET BOOKING OPTION SELECTOR */}
              <div className="space-y-1.5 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                <label className="block font-bold text-slate-800 text-[11px] uppercase tracking-wide">
                  Ticket Booking Facility Option *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setBookingStatusChoice('booked')}
                    className={`py-2 px-3 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 border transition-all cursor-pointer ${
                      bookingStatusChoice === 'booked'
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                    }`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Ticket Booked
                  </button>

                  <button
                    type="button"
                    onClick={() => setBookingStatusChoice('not_booked')}
                    className={`py-2 px-3 rounded-lg font-bold text-xs flex items-center justify-center gap-1.5 border transition-all cursor-pointer ${
                      bookingStatusChoice === 'not_booked'
                        ? 'bg-amber-600 text-white border-amber-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                    }`}
                  >
                    <AlertCircle className="w-3.5 h-3.5" /> Ticket Not Booked
                  </button>
                </div>
              </div>

              {/* CONDITIONAL BODY: TICKET NOT BOOKED */}
              {bookingStatusChoice === 'not_booked' ? (
                <div className="space-y-2 p-3 bg-amber-50/60 border border-amber-200 rounded-xl">
                  <div className="text-[11px] text-amber-900 font-semibold leading-relaxed">
                    Record that tickets are not booked at this time. The travel request remains approved and the employee can enter ticket details later if required.
                  </div>
                  <div>
                    <label className="block font-bold text-slate-800 mb-1">Reason for Not Booking Ticket *</label>
                    <textarea
                      rows={3}
                      value={bookingForm.not_booked_reason}
                      onChange={(e) => setBookingForm({ ...bookingForm, not_booked_reason: e.target.value })}
                      placeholder="e.g. Employee arranging own booking / Meeting tentative / Client booking / Train tickets to be purchased locally..."
                      className="w-full p-2.5 bg-white border border-slate-300 rounded-xl font-medium outline-none focus:border-amber-500"
                      required
                    />
                  </div>
                </div>
              ) : (
                /* CONDITIONAL BODY: TICKET BOOKED */
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">PNR / Reference Number *</label>
                      <input
                        type="text"
                        value={bookingForm.pnr_reference}
                        onChange={(e) => setBookingForm({ ...bookingForm, pnr_reference: e.target.value })}
                        className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-mono uppercase font-bold"
                        placeholder="PNR12345"
                        required
                      />
                    </div>
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Ticket Number</label>
                      <input
                        type="text"
                        value={bookingForm.ticket_number}
                        onChange={(e) => setBookingForm({ ...bookingForm, ticket_number: e.target.value })}
                        className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-mono"
                        placeholder="098-2345678"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Carrier / Flight / Train No *</label>
                      <input
                        type="text"
                        value={bookingForm.carrier_name_or_no}
                        onChange={(e) => setBookingForm({ ...bookingForm, carrier_name_or_no: e.target.value })}
                        className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium"
                        placeholder="6E-204"
                        required
                      />
                    </div>
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Booking Date *</label>
                      <input
                        type="date"
                        value={bookingForm.booking_date}
                        onChange={(e) => setBookingForm({ ...bookingForm, booking_date: e.target.value })}
                        className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-mono"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Base Fare (₹)</label>
                      <input
                        type="number"
                        value={bookingForm.base_fare}
                        onChange={(e) => setBookingForm({ ...bookingForm, base_fare: Number(e.target.value) })}
                        className="w-full h-9 px-2 bg-slate-50 border border-slate-300 rounded-xl font-mono font-bold"
                      />
                    </div>
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Taxes (₹)</label>
                      <input
                        type="number"
                        value={bookingForm.taxes}
                        onChange={(e) => setBookingForm({ ...bookingForm, taxes: Number(e.target.value) })}
                        className="w-full h-9 px-2 bg-slate-50 border border-slate-300 rounded-xl font-mono font-bold"
                      />
                    </div>
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Fees (₹)</label>
                      <input
                        type="number"
                        value={bookingForm.convenience_fee}
                        onChange={(e) => setBookingForm({ ...bookingForm, convenience_fee: Number(e.target.value) })}
                        className="w-full h-9 px-2 bg-slate-50 border border-slate-300 rounded-xl font-mono font-bold"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Select Corporate Credit Card</label>
                    <select
                      value={bookingForm.credit_card_used_id}
                      onChange={(e) => setBookingForm({ ...bookingForm, credit_card_used_id: e.target.value })}
                      className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium"
                    >
                      <option value="">-- Direct Corporate Invoice --</option>
                      {creditCards.filter((c) => c.status === 'active').map((c) => (
                        <option key={c._id} value={c._id}>
                          {c.card_name} ({c.card_number_masked}) — Avail: ₹ {(c.available_limit || 0).toLocaleString('en-IN')}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Ticket PDF / Attachment Link</label>
                    <input
                      type="text"
                      placeholder="e.g. Google Drive Link or Ticket URL"
                      value={bookingForm.ticket_attachment_url}
                      onChange={(e) => setBookingForm({ ...bookingForm, ticket_attachment_url: e.target.value })}
                      className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium"
                    />
                  </div>
                </>
              )}

              <div className="pt-3 border-t border-slate-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowBookingModal(false)}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 font-bold rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-5 py-2 font-extrabold rounded-xl shadow-md cursor-pointer transition-colors ${
                    bookingStatusChoice === 'not_booked'
                      ? 'bg-amber-600 hover:bg-amber-500 text-white'
                      : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  }`}
                >
                  {bookingStatusChoice === 'not_booked' ? 'Record Ticket Not Booked' : 'Confirm Ticket Booking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Travel Request Details Modal */}
      {detailsModalOpen && selectedRequestId && (
        <TravelRequestDetailsModal
          isOpen={detailsModalOpen}
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedRequestId(null);
          }}
          requestId={selectedRequestId}
          moduleType="outstation"
          onActionCompleted={() => {
            fetchAssignedRequests();
            fetchBookings();
            fetchRequests();
          }}
          currentUser={user}
        />
      )}
    </div>
  );
};

export default TravelBookingDesk;
